import { useEffect, useRef, useState } from 'react'
import { Crown } from 'lucide-react'
import { STAGE } from '@sdg/shared'
import { cn } from '../../lib/cn'
import { stageSound } from '../../lib/stageAudio'
import { CountdownDial } from './CountdownDial'
import type { DrawView } from '../admin/screenTypes'

/**
 * The slide-17 drum-roll, as a prize wheel.
 *
 * Count first, then spin — they do not overlap:
 *
 *   0.0s  10 · 9 · 8 … 1 over a still wheel
 *  10.0s  the wheel is released; names start flying past on the segments
 *  17.0s  the wheel settles with the winner under the pointer, and the
 *         fanfare starts
 *  19.0s  the card takes over and the confetti fires
 *
 * Every draw runs to this timing, the Grand Lucky Draw included — see
 * `drawCountdownMs` in `shared`.
 *
 * **The names are on the wheel, and they keep changing.** Two earlier versions
 * were wrong in opposite directions. Fixed names on the rim told the hall that
 * those twelve students were the ones in the running, when more than two
 * thousand are. Blank segments with one name shuffling in a plate over the
 * middle fixed the fairness and lost the wheel — it stopped looking like a
 * draw at all. Now every segment carries a name and the whole set re-deals
 * several times a second, slowing as the wheel slows: hundreds of names go
 * past, and no one is singled out until the pointer stops.
 *
 * **The wheel does not decide anything.** The winner is drawn on the server by
 * `crypto.getRandomValues` before this component ever renders; the name is
 * already in the payload that triggers the spin. The wheel is the drum-roll,
 * not the draw — which is why the last deal can put the winner's name on the
 * segment the pointer is going to stop at.
 */

/*
 * The timings come from `shared` because the server uses the same numbers to
 * refuse a second draw while this one is still on the projector. If the two
 * drifted apart, Admin could start a draw mid-spin and the hall would watch
 * the name change under the pointer.
 */
const SPIN_MS = STAGE.drawSpinMs

/** Segments. Twenty-four is a fairground wheel and still fits a first name. */
const SEGMENTS = 24
/** The segment the pointer stops at, so the last deal knows where to put the winner. */
const WINNER_SEGMENT = 0
/**
 * Whole turns before it settles — enough that the start is a blur.
 *
 * Scaled with the spin: at ten turns over seven seconds the wheel covers only
 * 1.3 turns in its first second and reads as sluggish, where the old
 * five-second spin managed 1.9. Fourteen restores that opening speed over the
 * longer run.
 */
const TURNS = 14

/**
 * How the wheel sheds speed across the spin.
 *
 * This was `cubic-bezier(0.17, 0.05, 0.1, 1)`, which put **92% of the
 * rotation into the first three seconds** and left the last two as a crawl
 * too slow to see. The transition ran its full five seconds; the wheel just
 * looked like it had stopped after two.
 *
 * Measured in fifths of the spin, this curve is at 19 / 46 / 74 / 94 / 100
 * per cent — still clearly decelerating, but with a quarter of the travel
 * left in the final fifth, so the wheel is visibly turning throughout.
 */
const SPIN_EASING = 'cubic-bezier(0.45, 0.35, 0.6, 1)'

export type SpinPhase = 'countdown' | 'spinning' | 'done'

/**
 * Drives the sequence: which phase it is in and what number is on screen.
 *
 * `countdownMs` is passed in rather than read from `STAGE` directly. It
 * comes from `drawCountdownMs(drawNo)`, which the server's stage guard reads
 * as well, so the wheel and the refusal cannot disagree about how long this
 * draw takes — and a draw that needs its own timing later changes in one
 * place rather than in both.
 */
export function useSpinSequence(
  active: boolean,
  countdownMs: number,
): { phase: SpinPhase; count: number } {
  const [elapsed, setElapsed] = useState(0)
  const started = useRef(0)

  /*
   * A timer, not `requestAnimationFrame`.
   *
   * rAF is suspended in a background tab, and the hall's laptop is not
   * guaranteed to have this window in front — the first version froze on "3"
   * and never released the wheel the moment the tab lost focus. Timers keep
   * running (throttled, but they run), so the draw completes either way.
   *
   * 100ms is also the right resolution: the countdown changes once a second
   * and the spin itself is a CSS transition the compositor drives, so there
   * is nothing here that needs sixty frames a second of React.
   */
  useEffect(() => {
    if (!active) {
      setElapsed(0)
      return
    }

    started.current = Date.now()
    setElapsed(0)

    const timer = window.setInterval(() => setElapsed(Date.now() - started.current), 100)
    return () => window.clearInterval(timer)
  }, [active])

  const phase: SpinPhase = active && elapsed < countdownMs ? 'countdown' : 'spinning'
  // 5, 4, 3, 2, 1 — or 10 down to 1 on the Grand Draw. Never 0.
  const count = Math.max(1, Math.ceil((countdownMs - elapsed) / 1000))

  /*
   * One tick as each new second starts.
   *
   * Fired on the change of the *displayed* number rather than on the 100ms
   * timer, so it sounds once a second and not ten times. Tying it to the
   * number is also what guarantees the sound and the dial agree: an earlier
   * version stretched one long recording across the whole count, and the
   * beeps drifted against the numbers because nothing held them together.
   *
   * In an effect, not inline: this renders on every tick of the timer, and
   * starting a sound during render would fire again on any re-render React
   * decided to do.
   */
  const counting = active && phase === 'countdown'
  const ticked = useRef<number | null>(null)
  useEffect(() => {
    if (!counting) {
      ticked.current = null
      return
    }
    if (ticked.current === count) return
    ticked.current = count
    stageSound.countdownTick(count)
  }, [counting, count])

  if (!active) return { phase: 'done', count: 0 }

  return { phase, count }
}

/**
 * The wheel's own colours — jewel segments and gold, the same for every draw.
 *
 * A real prize wheel does not change colour between rounds, so only the rim
 * and hub take the draw's theme (slide 16's "own colour theme"); the stage
 * behind it already carries that anyway.
 */
const SEGMENT_COLOURS = ['#A4123F', '#15427E', '#0E6B4E', '#5B2A86', '#B3561B', '#1A6E86'] as const

const ACCENTS = {
  navy: { rim: '#E6A23C', rimDeep: '#9C6714', hub: '#F2C75C' },
  purple: { rim: '#FFD16A', rimDeep: '#A87A1C', hub: '#FFDE92' },
  gold: { rim: '#FFD16A', rimDeep: '#A87A1C', hub: '#FFE7A6' },
} as const

/** Degrees -> a point on a circle of radius `r`, measured from 12 o'clock. */
function onCircle(angle: number, r: number) {
  const rad = ((angle - 90) * Math.PI) / 180
  return { x: r * Math.cos(rad), y: r * Math.sin(rad) }
}

/** A fresh set of names for the segments, taken from anywhere in the pool. */
function deal(names: string[]): string[] {
  if (names.length === 0) return Array.from({ length: SEGMENTS }, () => '')

  const start = Math.floor(Math.random() * names.length)
  return Array.from({ length: SEGMENTS }, (_, i) => names[(start + i) % names.length])
}

/**
 * Re-deals the segment names, faster at the start and slowing with the wheel,
 * ending on a deal that has the winner on the landing segment.
 *
 * A chain of timeouts with a growing delay rather than one interval: the point
 * is that it decelerates. An even flicker that stops dead reads as a list
 * refreshing rather than a draw settling.
 */
function useDealing(names: string[], winner: string, rolling: boolean): string[] {
  const [labels, setLabels] = useState<string[]>([])

  useEffect(() => {
    if (!rolling) {
      setLabels([])
      return
    }

    let elapsed = 0
    let timer = 0

    const land = () => {
      const final = deal(names)
      final[WINNER_SEGMENT] = winner || final[WINNER_SEGMENT]
      setLabels(final)
    }

    const tick = () => {
      setLabels(deal(names))

      const progress = Math.min(1, elapsed / SPIN_MS)
      // 70ms between deals at the start, about 450ms by the end.
      const delay = 70 + 380 * progress * progress
      elapsed += delay

      if (elapsed < SPIN_MS) timer = window.setTimeout(tick, delay)
      else land()
    }

    setLabels(deal(names))
    timer = window.setTimeout(tick, 70)

    return () => window.clearTimeout(timer)
  }, [rolling, names, winner])

  return labels
}

export function DrawWheel({
  names,
  winner,
  theme,
  label,
  count,
  countdownMs,
  phase,
}: {
  /** First names from the open draw's pool — what flies past on the segments. */
  names: string[]
  /** The winner's first name. The last deal always lands it under the pointer. */
  winner: string
  theme: DrawView['theme']
  label: string
  count: number
  /** This draw's countdown; the Grand Draw's is twice the others'. */
  countdownMs: number
  phase: SpinPhase
}) {
  const accent = ACCENTS[theme]
  const R = 100
  const step = 360 / SEGMENTS

  const spinning = phase === 'spinning'
  const labels = useDealing(names, winner, spinning)

  /*
   * The freewheel, for the length of the spin.
   *
   * Driven from the phase rather than from the deal loop: it is one
   * continuous recording slowing down, not a click per name, so it starts
   * once when the wheel is released and is cut short if the screen changes
   * under it.
   */
  useEffect(() => {
    if (!spinning) return
    stageSound.wheelStart(SPIN_MS)
    return () => stageSound.wheelStop()
  }, [spinning])

  /*
   * Where it stops. Half a slice back from a whole number of turns, so the
   * landing segment's centre — not its edge — ends under the pointer.
   */
  const landing = TURNS * 360 - step / 2

  /*
   * Released when the countdown finishes, not when the component mounts.
   *
   * Two separate reasons for the delay. The wheel is only mounted while a
   * draw is running, so rendering it straight to its landing angle gives the
   * browser nothing to animate *from* — a newly mounted element starts at its
   * final computed style and the transition never runs, which is exactly what
   * made the first version look like a photograph of a wheel. And the
   * Committee wants the count to finish before anything moves.
   *
   * A timer rather than `requestAnimationFrame`: rAF does not fire at all in
   * a background tab, and the hall's laptop is not guaranteed to have this
   * window in front.
   */
  const [released, setReleased] = useState(false)

  useEffect(() => {
    if (!spinning) {
      setReleased(false)
      return
    }

    const timer = window.setTimeout(() => setReleased(true), 50)
    return () => window.clearTimeout(timer)
  }, [spinning])

  return (
    <div className="relative grid place-items-center">
      {/* The flapper, fixed at 12 o'clock while the wheel turns beneath it. */}
      <svg
        viewBox="0 0 26 26"
        className="absolute top-[-2.6vmin] z-30 w-[6vmin] drop-shadow-[0_3px_8px_rgba(10,5,30,0.55)]"
        aria-hidden
      >
        <path
          d="M13 25 L2 6 C1 4 2 2 4 2 L22 2 C24 2 25 4 24 6 Z"
          fill={accent.rim}
          stroke={accent.rimDeep}
          strokeWidth={1.4}
        />
        <circle cx="13" cy="7.5" r="2.6" fill={accent.rimDeep} />
      </svg>

      <svg viewBox="-124 -124 248 248" className="w-[46vmin]" role="img" aria-label={label}>
        {/* The cabinet the wheel sits in, and its ring of bulbs. */}
        <circle cx={0} cy={0} r={118} fill={accent.rimDeep} />
        <circle cx={0} cy={0} r={118} fill="none" stroke={accent.rim} strokeWidth={3} />
        {Array.from({ length: SEGMENTS }, (_, i) => {
          const p = onCircle(i * step + step / 2, 109)
          return (
            <circle
              key={i}
              cx={p.x}
              cy={p.y}
              r={4}
              fill={i % 2 === 0 ? '#FFF4D2' : accent.hub}
              opacity={i % 2 === 0 ? 0.95 : 0.7}
            />
          )
        })}

        <g
          style={{
            transform: `rotate(${released ? landing : 0}deg)`,
            transformOrigin: '0 0',
            transition: released
              ? `transform ${SPIN_MS}ms ${SPIN_EASING}`
              : 'none',
          }}
        >
          {Array.from({ length: SEGMENTS }, (_, i) => {
            const p0 = onCircle(i * step, R)
            const p1 = onCircle((i + 1) * step, R)
            const mid = i * step + step / 2
            const upright = mid < 180
            const tx = R * 0.9
            const name = labels[i] ?? ''

            return (
              <g key={i}>
                <path
                  d={`M 0 0 L ${p0.x.toFixed(2)} ${p0.y.toFixed(2)} A ${R} ${R} 0 0 1 ${p1.x.toFixed(2)} ${p1.y.toFixed(2)} Z`}
                  fill={SEGMENT_COLOURS[i % SEGMENT_COLOURS.length]}
                  stroke={accent.rim}
                  strokeWidth={0.9}
                />
                {name && (
                  <text
                    transform={`rotate(${mid - 90}) ${upright ? '' : `rotate(180, ${tx}, 0)`}`}
                    x={tx}
                    y={0}
                    textAnchor={upright ? 'end' : 'start'}
                    dominantBaseline="central"
                    fontSize={7.6}
                    fontWeight={700}
                    fill="#FFFFFF"
                  >
                    {name.slice(0, 10)}
                  </text>
                )}
              </g>
            )
          })}
        </g>

        {/* The rim and the hub do not turn — a still frame makes the spin read
            faster, and the hub is where the eye rests. */}
        <circle cx={0} cy={0} r={R} fill="none" stroke={accent.rim} strokeWidth={5} />
        <circle cx={0} cy={0} r={R - 4} fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth={1} />
        <circle cx={0} cy={0} r={26} fill={accent.rimDeep} />
        <circle cx={0} cy={0} r={22} fill={accent.hub} />
      </svg>

      <Crown
        className="pointer-events-none absolute z-20 size-[4.4vmin] text-[#7A4D0C]"
        strokeWidth={2.2}
        fill="currentColor"
        fillOpacity={0.18}
        aria-hidden
      />

      {phase === 'countdown' && (
        <div className="pointer-events-none absolute inset-0 z-40 grid place-items-center">
          {/* The dial sits over the still wheel, on a disc dark enough to
              read the ring against the slices behind it. */}
          <div className="grid size-[34vmin] place-items-center rounded-full bg-[#0A0F24]/82 backdrop-blur-sm">
            <CountdownDial
              seconds={count}
              total={Math.ceil(countdownMs / 1000)}
              size="30vmin"
            />
          </div>
        </div>
      )}
    </div>
  )
}
